import { applySnapshot, types } from "mobx-state-tree"
import { flow } from "modules/common/models/flow"
import { Notificator } from "modules/common/models/notificator"
import { Transport } from "modules/common/models/transport"
import { apiUrls } from "modules/common/services/communication/urls"

export const OneCEntityMapping = types
  .model({
    resource: types.string,
    fieldMap: types.frozen<TStringMap<string>>({}),
    relatedResources: types.frozen<TStringMap<string>>({}),
  })
  .named("OneCEntityMapping")

export const OneCMapping = types
  .model({
    departments: OneCEntityMapping,
    positions: OneCEntityMapping,
    employees: OneCEntityMapping,
    staff: OneCEntityMapping,
    saved: types.optional(types.boolean, false),
  })
  .named("OneCMapping")

export interface OneCPreviewItem {
  externalId: string | null
  sourceName: string | null
  isDeleted: boolean
  status: "new" | "linked" | "candidate" | "onlyInRost"
  targetId: string | null
  targetName: string | null
  birthday: string | null
  suggestedPositionId: string | null
  suggestedPositionName: string | null
  /** подразделение из кадровой истории 1С, если оно связано с 1С; пусто - возьмётся отдел должности */
  departmentName?: string | null
  /** штат: почему строку не импортировать (подразделение/должность не связаны с 1С) */
  problem?: string | null
}

export interface OneCRostEntity {
  id: string
  name: string
  group: string | null
}

export interface OneCPreviewResult {
  items: OneCPreviewItem[]
  rostEntities: OneCRostEntity[]
  positions: OneCRostEntity[]
  newCount: number
  linkedCount: number
  candidateCount: number
  onlyInRostCount: number
  resource: string
}

export interface OneCImportDecision {
  externalId: string
  targetId: string
  positionTargetId?: string
}

export interface OneCImportResult {
  created: number
  updated: number
  linked: number
  skipped: number
  /** подразделений встало в иерархию по родителю из 1С */
  nested?: number
  warnings?: string[]
}

export const OneCStore = types
  .compose(
    Transport,
    Notificator,
    types.model({
      resources: types.array(types.string),
      fields: types.array(types.string),
      mapping: types.maybeNull(OneCMapping),
      preview: types.frozen<OneCPreviewResult | null>(null),
      loadingResources: types.boolean,
      loadingFields: types.boolean,
      previewing: types.boolean,
      importing: types.boolean,
    })
  )
  .actions((self) => ({
    loadResources: flow(function* () {
      try {
        self.loadingResources = true
        const list = yield self.transport.get<string[]>(apiUrls.onec.resources)
        self.resources.replace(list)
        return true
      } catch (er) {
        self.notify.error(er)
        return false
      } finally {
        self.loadingResources = false
      }
    }),

    loadFields: flow(function* (resource: string) {
      try {
        self.loadingFields = true
        const list = yield self.transport.get<string[]>(apiUrls.onec.resourceFields(resource))
        self.fields.replace(list)
        return true
      } catch (er) {
        self.notify.error(er)
        self.fields.replace([])
        return false
      } finally {
        self.loadingFields = false
      }
    }),

    loadMapping: flow(function* () {
      try {
        const snapshot = yield self.transport.get<OneCMappingSnapshotType>(apiUrls.onec.mapping)
        self.mapping = OneCMapping.create(snapshot)
        return true
      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),

    saveMapping: flow(function* (model: OneCMappingSnapshotType) {
      try {
        const snapshot = yield self.transport.post<OneCMappingSnapshotType>(apiUrls.onec.mapping, model)
        if (self.mapping) {
          applySnapshot(self.mapping, snapshot)
        } else {
          self.mapping = OneCMapping.create(snapshot)
        }
        self.notify.success("Сопоставление с 1С сохранено")
        return true
      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),

    previewImport: flow(function* (entityKey: string) {
      try {
        self.previewing = true
        const result = yield self.transport.get<OneCPreviewResult>(apiUrls.onec.preview(entityKey))
        self.preview = result
        return true
      } catch (er) {
        self.notify.error(er)
        self.preview = null
        return false
      } finally {
        self.previewing = false
      }
    }),

    applyImport: flow(function* (entityKey: string, decisions: OneCImportDecision[]) {
      try {
        self.importing = true
        const result: OneCImportResult = yield self.transport.post<OneCImportResult>(
          apiUrls.onec.import(entityKey),
          decisions
        )
        self.notify.success(
          `Импорт завершён: создано ${result.created}, обновлено ${result.updated}, связано ${result.linked}, пропущено ${result.skipped}` +
            (result.nested ? `, в иерархии ${result.nested}` : "")
        )
        ;(result.warnings ?? []).forEach((w) => self.notify.warning(w))
        return true
      } catch (er) {
        self.notify.error(er)
        return false
      } finally {
        self.importing = false
      }
    }),

    clearPreview() {
      self.preview = null
    },
  }))

export const initialState = (): OneCStoreSnapshotType => ({
  resources: [],
  fields: [],
  mapping: null,
  preview: null,
  loadingResources: false,
  loadingFields: false,
  previewing: false,
  importing: false,
})

export type OneCStoreType = typeof OneCStore.Type
export type OneCStoreSnapshotType = typeof OneCStore.SnapshotType
export type OneCMappingType = typeof OneCMapping.Type
export type OneCMappingSnapshotType = typeof OneCMapping.SnapshotType
export type OneCEntityMappingSnapshotType = typeof OneCEntityMapping.SnapshotType
